<template>
	<portal :to="portal">
		<Trap :disabled="!showTrap || !shouldTrap">
			<helio-pop
				v-if="mobileOverlay ? !isMobile : true"
				:active="active"
				:anchor="anchor"
				:gap="gap"
				:closeable="closeable"
				:close-on-outside-click="closeOnOutsideClick"
				:title="title"
				:arrow="arrow"
				:parent-class="popClassName"
				:parent-position="parentPosition"
				:initial-position="position"
				:initial-offset="offset"
				:class="compiledClasses"
				:width="width"
				skin="light"
				shadow="medium"
				@close="$emit('close')"
				@open="$emit('open')"
			>
				<template slot="content">
					<slot name="content" />
				</template>

				<template
					v-if="hasFooterSlot"
					slot="footer"
				>
					<slot name="footer" />
				</template>
			</helio-pop>

			<helio-overlay
				v-if="active && isMobile && mobileOverlay"
				:class="customClasses"
				:closeable="true"
				:title="title"
				@close="$emit('close')"
				@open="$emit('open')"
			>
				<template slot="content">
					<slot name="content" />
				</template>

				<template
					v-if="hasFooterSlot"
					slot="footer"
				>
					<slot name="footer" />
				</template>
			</helio-overlay>
		</Trap>
	</portal>
</template>

<script>
import Trap from 'vue-focus-lock';
import Pop from '../pop/Pop.vue';

export default {
	name: 'Popup',

	components: {
		Trap,
		'helio-pop': Pop,
	},

	props: {
		active: {
			type: Boolean,
			required: true,
			description: 'Sets whether the Popup will be displayed',
		},

		anchor: {
			type: String,
			required: true,
			description: 'Sets which element will control the Popup\'s position',
		},

		arrow: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether the Popup has an arrow pointing out',
		},

		closeOnOutsideClick: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Sets whether a user can close the Popup by clicking elsewhere on the page',
		},

		closeable: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether the Popup can be closed by a user',
		},

		customClasses: {
			type: String,
			required: false,
			default: null,
			description: 'Sets custom CSS classes on the Popup',
		},

		gap: {
			type: Number,
			required: false,
			default: 30,
			description: 'Sets the distance between the Popup and its anchor element',
		},

		mobileOverlay: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Sets whether the mobile version of the Popup will be shown',
		},

		mobileOverlayThreshold: {
			type: Number,
			required: false,
			default: 720,
			description: 'Sets at which resolution the mobile version of the Popup will be shown',
		},

		offset: {
			type: String,
			required: false,
			default: 'center',
			validator: value => [
				'bottom',
				'center',
				'left',
				'right',
				'top',
			].indexOf(value) !== -1,
			description: 'Sets the offset of the Popup in relation to its anchor element',
		},

		parentPosition: {
			type: String,
			required: false,
			default: 'normal',
			description: 'Provides context about the parent\'s position. This context determines how the Pop component will position the Popup.',
		},

		portal: {
			type: String,
			required: false,
			default: 'pop',
			description: 'Sets the target portal to use',
		},

		position: {
			type: String,
			required: false,
			default: 'top',
			validator: value => [
				'left',
				'bottom',
				'top',
				'right',
			].indexOf(value) !== -1,
			description: 'Sets where the Popup will appear in relation to its anchor element',
		},

		shouldTrap: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Sets whether the user\'s focus will be trapped inside the Popup during keyboard navigation',
		},

		title: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the Popup\'s title',
		},

		width: {
			type: Number,
			required: false,
			default: null,
			description: 'Sets a custom width for the Popup',
		},
	},

	slots: {
		content: {
			required: false,
			scoped: false,
			description: 'Supplies the content of the Popup component',
		},

		footer: {
			required: false,
			scoped: false,
			description: 'Supplies the content of the Footer component',
		},
	},

	data() {
		return {
			popClassName: '',
			showTrap: false,
			lastFocusedElement: null,
			isMobile: false,
		};
	},

	computed: {
		hasFooterSlot() {
			return !!this.$slots.footer || !!this.$scopedSlots.footer;
		},

		compiledClasses() {
			let compiledClasses = 'popup ';

			if (this.customClasses) {
				compiledClasses += `${this.customClasses} `;
			}

			return compiledClasses;
		},
	},

	watch: {
		active() {
			if (this.active) {
				this.showTrap = this.active;
				this.lastFocusedElement = document.activeElement;
			}

			if (!this.active) {
				this.showTrap = this.active;
				if (this.shouldTrap) {
					setTimeout(() => {
						this.lastFocusedElement.focus();
					}, 100);
				}
			}
		},
	},

	mounted() {
		window.addEventListener('keyup', this.checkForEscape);
		window.addEventListener('resize', this.onResize);
		this.onResize();

		if (this.$el && this.$el.classList && this.$el.classList.value) {
			this.popClassName = this.$el.classList.value.replace('v-portal', '');
		}
	},

	beforeDestroy() {
		window.removeEventListener('keyup', this.checkForEscape);
		window.removeEventListener('resize', this.onResize);
	},

	methods: {
		checkForEscape(event) {
			const { keyCode } = event;

			if (keyCode === 27 && this.closeable) {
				this.close();
			}
		},

		close(emit = true) {
			this.showTrap = false;

			if (this.shouldTrap) {
				setTimeout(() => {
					this.lastFocusedElement.focus();
				}, 100);
			}

			if (emit) {
				this.$emit('close');
			}
		},

		onResize() {
			this.isMobile = window.innerWidth < this.mobileOverlayThreshold;
		},
	},
};
</script>

<style lang="scss" src="./popup.scss" />
